import { useCallback, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'

import { extractInputProps, type TypeaheadOptionType } from '@/defs/input'
import { useTypeaheadKeyboardNav } from '@/hooks/useTypeaheadKeyboardNav'
import { mergeRefs, toArray } from '@/utils/commonUtil'
import { filterTypeahead } from '@/utils/typeaheadUtil'

import { type Props as ContentProps } from './InputTypeaheadContent'

type Props<T extends string = string> = {
  closeOnSelect?: boolean
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
} & ContentProps<T>

const EMPTY: never[] = []

export const useTypeaheadContent = <T extends string = string>({
  autoFocus,
  closeOnSelect,
  disabled,
  icon = 'search',
  isOpen,
  loading,
  minCharacters = 0,
  multiselect,
  onChange,
  onClear,
  onSearch,
  onSelect,
  optionGroups: optionGroupsProp,
  optionReset,
  options,
  placeholder,
  ref: refProp,
  required,
  setIsOpen,
  showClear = true,
  showLoadingMask,
  value,
  ...props
}: Props<T>) => {
  const { inputProps } = extractInputProps(props)
  const [search, setSearchInternal] = useState<string>('')

  const setSearch = useCallback(
    (next: string) => {
      setSearchInternal(next)
      onSearch?.(next)
    },
    [onSearch],
  )

  const optionGroups = useMemo(
    () => [...(optionGroupsProp || []), ...(options ? [{ options }] : [])],
    [optionGroupsProp, options],
  )

  const optionsAll = useMemo(() => optionGroups.flatMap((g) => g.options), [optionGroups])

  const values = useMemo((): T[] => (value ? toArray(value) : EMPTY), [value])

  const selections = useMemo(
    (): TypeaheadOptionType<T>[] => values.map((v) => optionsAll.find((o) => o.value === v) || { value: v }),
    [optionsAll, values],
  )

  const isEmpty = values.length === 0

  const { groups, resultsCount } = useMemo(
    () =>
      filterTypeahead({
        disableFiltering: !!onSearch,
        minCharacters,
        optionGroups,
        optionReset,
        search,
        showReset: !isEmpty,
      }),
    [onSearch, minCharacters, search, optionGroups, optionReset, isEmpty],
  )

  const { onKeyDownButton, onKeyDownInput, refInput, setRefButton } = useTypeaheadKeyboardNav(resultsCount)

  const ref = useMemo(() => mergeRefs(refProp, refInput), [refProp, refInput])

  const findLabel = (value: T) => optionsAll.find((o) => o.value === value)?.label

  const onOpen = () => {
    if (disabled) return
    // For iOS, lets the modal input's autoFocus run in the same tap handler which opens the modal
    flushSync(() => setIsOpen(true))
  }

  const onSelectInternal = (selected: T) => {
    if (disabled) return
    setSearch('')
    if (!multiselect || closeOnSelect) setIsOpen(false)

    if (!multiselect) {
      onSelect?.(selected, findLabel(selected))
      return
    }

    const selectedNew = values.includes(selected) ? values.filter((v) => v !== selected) : [...values, selected]
    onChange(selectedNew.map((value) => ({ label: findLabel(value), value })))
  }

  const onRemoveLast = () => {
    const last = values.at(-1)
    if (multiselect && last !== undefined) onSelectInternal(last)
  }

  return {
    'aria-label': props['aria-label'],
    autoFocus,
    disabled,
    groups,
    hasMinCharacters: search.length >= minCharacters,
    hasResults: groups.length > 0,
    icon,
    inputProps,
    isEmpty,
    isOpen,
    loading,
    multiselect,
    onClear,
    onKeyDownButton,
    onKeyDownInput,
    onOpen,
    onRemoveLast,
    onSelect: onSelectInternal,
    placeholder,
    ref,
    refInput,
    required,
    search,
    selections,
    setIsOpen,
    setRefButton,
    setSearch,
    showClear,
    showLoadingMask,
    values,
  }
}
